import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import { decrementPendingActionsCount } from "@/features/auth";
import type {
  GenerateTimesheetDto,
  UpdateTimesheetEntryDto,
  UpdateWorkTimeBalanceDto,
  TimesheetResponse,
  TimesheetEntryResponse,
  TimesheetAllocationResponse,
  SetAllocationResponse,
  CreateTimesheetAllocationDto,
  BulkAllocationRequest,
  CombinedTimesheetDetailResponse,
  CombinedTimesheetListResponse,
  PreviousAllocationsResponse,
} from "../types";

export const timesheetsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTimesheets: builder.query<TimesheetResponse[], void>({
      query: () => ({
        url: `/api/accounting/timesheets/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "Timesheets" as const, id: "LIST" }],
    }),
    getTimesheetDetail: builder.query<TimesheetResponse, number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Timesheets" as const, id }],
    }),
    generateTimesheet: builder.mutation<TimesheetResponse, GenerateTimesheetDto>({
      query: (body) => ({
        url: `/api/accounting/timesheets/generate/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "Timesheets" as const, id: "LIST" }],
    }),
    approveTimesheet: builder.mutation<TimesheetResponse, number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/approve/`,
        method: "POST",
        body: {},
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Timesheets" as const, id },
        { type: "Timesheets" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),
    deleteTimesheet: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: [{ type: "Timesheets" as const, id: "LIST" }],
    }),
    updateTimesheetEntry: builder.mutation<TimesheetEntryResponse, { id: number; body: UpdateTimesheetEntryDto }>({
      query: ({ id, body }) => ({
        url: `/api/accounting/timesheet-entries/${id}/`,
        method: "PATCH",
        body,
        credentials: "include",
      }),
      invalidatesTags: () => {
        return [{ type: "Timesheets" as const, id: "LIST" }];
      },
    }),
    downloadTimesheetPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/preview/`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),
    downloadTimesheetPdf: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/preview/?output=pdf`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),
    getTimesheetAllocations: builder.query<TimesheetAllocationResponse[], number>({
      query: (id) => ({
        url: `/api/accounting/timesheets/${id}/allocations/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "TimesheetAllocations" as const, id: `TIMESHEET-${id}` }],
    }),
    createTimesheetAllocation: builder.mutation<
      SetAllocationResponse,
      { timesheetId: number; body: CreateTimesheetAllocationDto }
    >({
      query: ({ timesheetId, body }) => ({
        url: `/api/accounting/timesheets/${timesheetId}/allocations/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        { type: "TimesheetAllocations" as const, id: `TIMESHEET-${timesheetId}` },
      ],
    }),
    updateWorkTimeBalance: builder.mutation<TimesheetResponse, { id: number; body: UpdateWorkTimeBalanceDto }>({
      query: ({ id, body }) => ({
        url: `/api/accounting/timesheets/${id}/work-time-balance/`,
        method: "PATCH",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Timesheets" as const, id },
        { type: "Timesheets" as const, id: "LIST" },
      ],
    }),
    deleteTimesheetAllocation: builder.mutation<void, { timesheetId: number; allocationId: number }>({
      query: ({ timesheetId, allocationId }) => ({
        url: `/api/accounting/timesheets/${timesheetId}/allocations/${allocationId}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        { type: "TimesheetAllocations" as const, id: `TIMESHEET-${timesheetId}` },
      ],
    }),
    bulkUpdateWorkerAllocations: builder.mutation<
      TimesheetAllocationResponse[],
      { timesheetId: number; workerId: number; body: BulkAllocationRequest }
    >({
      query: ({ timesheetId, workerId, body }) => ({
        url: `/api/accounting/timesheets/${timesheetId}/workers/${workerId}/allocations/`,
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { timesheetId }) => [
        { type: "TimesheetAllocations" as const, id: `TIMESHEET-${timesheetId}` },
      ],
    }),
    getCombinedTimesheets: builder.query<CombinedTimesheetListResponse, void>({
      query: () => ({
        url: "/api/accounting/combined-timesheets/?page_size=100",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [
        { type: "Timesheets" as const, id: "LIST" },
        { type: "Payrolls" as const, id: "LIST" },
      ],
    }),
    getCombinedTimesheetDetail: builder.query<CombinedTimesheetDetailResponse, number>({
      query: (id) => ({
        url: `/api/accounting/combined-timesheets/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [
        { type: "Timesheets" as const, id },
        { type: "Payrolls" as const, id: "LIST" },
        { type: "TimesheetAllocations" as const, id: `TIMESHEET-${id}` },
      ],
    }),
    getPreviousAllocations: builder.query<PreviousAllocationsResponse, number>({
      query: (timesheetId) => ({
        url: `/api/accounting/timesheets/${timesheetId}/previous-allocations/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useGetTimesheetsQuery,
  useGetTimesheetDetailQuery,
  useLazyGetTimesheetDetailQuery,
  useGenerateTimesheetMutation,
  useApproveTimesheetMutation,
  useDeleteTimesheetMutation,
  useUpdateTimesheetEntryMutation,
  useDownloadTimesheetPreviewMutation,
  useDownloadTimesheetPdfMutation,
  useGetTimesheetAllocationsQuery,
  useCreateTimesheetAllocationMutation,
  useDeleteTimesheetAllocationMutation,
  useUpdateWorkTimeBalanceMutation,
  useBulkUpdateWorkerAllocationsMutation,
  useGetCombinedTimesheetsQuery,
  useGetCombinedTimesheetDetailQuery,
  useLazyGetPreviousAllocationsQuery,
} = timesheetsApi;

